import React from 'react';
import cn from 'classnames';
import { Avatar, Badge, Button } from '@odin-labs/components';
import { getInitialsForUser } from 'utils';
import { EditableEnhancedFormSubmissionWorker } from 'containers/jobsiteFormSubmission/types';
import {
  BriefcaseIcon,
  ClockIcon,
  IdBadgeIcon,
  ShieldCheckIcon,
  TimesIcon,
} from 'components/icons';

export const classes = {
  itemContainer: cn(
    'odin-flex odin-items-start odin-min-w-0 odin-p-1',
    'odin-border odin-border-gray-200 odin-rounded-lg odin-bg-white',
  ),
  workerInfo: (isClickable: boolean): string =>
    cn(
      'odin-flex odin-flex-1 odin-items-start odin-min-w-0 odin-space-x-3',
      'odin-py-2 odin-pl-2 odin-pr-1 odin-rounded-md odin-text-left',
      isClickable && 'hover:odin-bg-gray-50',
    ),
  avatarContainer: cn('odin-relative odin-flex-none'),
  workerDetails: cn(
    'odin-flex odin-flex-col odin-flex-1 odin-min-w-0 odin-space-y-1',
  ),
  // the participant type wraps under a long name
  workerNameRow: cn(
    'odin-flex odin-flex-wrap odin-items-center odin-gap-x-2 odin-gap-y-1 odin-min-w-0',
  ),
  workerName: cn(
    'odin-min-w-0 odin-text-base odin-font-medium odin-text-gray-900 odin-truncate',
  ),
  workerInfoContainer: cn(
    'odin-flex odin-flex-wrap odin-gap-x-3.5 odin-gap-y-1 odin-min-w-0',
  ),
  workerInfoItem: cn(
    'worker-info-item-container',
    'odin-flex',
    'odin-items-center',
    'odin-min-w-0',
    'odin-text-sm',
  ),
  workerInfoItemIcon: cn(
    'worker-info-item-icon',
    'odin-flex-none',
    'odin-text-odin-primary',
    'odin-text-sm',
    'odin-font-normal',
    'odin-mr-1.5',
  ),
  // a 44px tap target; the naked button fills it
  removeButtonContainer: cn(
    'odin-flex odin-flex-none odin-items-center odin-justify-center odin-w-11 odin-h-11',
  ),
  removeButton: cn('odin-w-full odin-justify-center'),
  clockIcon: cn(
    'odin-absolute -odin-right-0.5 -odin-bottom-0.5',
    'odin-text-[0.625rem] odin-p-0.5',
    'odin-bg-odin-success odin-text-white odin-rounded-full odin-ring-2 odin-ring-white',
  ),
};

export type FormSubmissionWorkerProps = {
  jfsWorker: EditableEnhancedFormSubmissionWorker;
  onRemoveIconClick?: () => void;
  onClick?: () => void;
  /** Shows the jobsite and participant type (default `true`). */
  showDetails?: boolean;
};

export function FormSubmissionWorker(
  props: FormSubmissionWorkerProps,
): React.ReactElement {
  const { jfsWorker, onRemoveIconClick, onClick, showDetails = true } = props;

  const { extraData, jobsiteWorker } = jfsWorker ?? {};
  const { participantType } = (extraData as { participantType: string }) ?? {};
  const {
    contractorWorker: { worker },
    stickerNumber: badgeId,
    jobsiteContractor,
    isOnSite,
  } = jobsiteWorker ?? {};
  const { name: jobsiteName } = jobsiteContractor?.jobsite ?? {};
  const { user, quickCode, profilePicture } = worker ?? {};
  const { firstName, lastName } = user?.identity ?? {};
  const { downloadUrl: imageUrl } = profilePicture ?? {};
  const name = `${firstName} ${lastName}`;

  const workerInfo = (
    <>
      <div className={classes.avatarContainer} aria-hidden="true">
        <Avatar
          size="md"
          src={imageUrl}
          placeholder={getInitialsForUser(user)}
          objectFit="cover"
        />
        {isOnSite && <ClockIcon className={classes.clockIcon} />}
      </div>
      {isOnSite && <span className="odin-sr-only">On site</span>}
      <div className={classes.workerDetails}>
        <div className={classes.workerNameRow}>
          <span className={classes.workerName}>{name}</span>
          {showDetails && participantType && (
            <Badge text={participantType} color="red" />
          )}
        </div>
        <div className={classes.workerInfoContainer}>
          <span className={classes.workerInfoItem}>
            <ShieldCheckIcon
              className={classes.workerInfoItemIcon}
              aria-hidden="true"
            />
            {quickCode}
          </span>
          {badgeId && (
            <span className={classes.workerInfoItem}>
              <IdBadgeIcon
                className={classes.workerInfoItemIcon}
                aria-hidden="true"
              />
              {badgeId}
            </span>
          )}
          {showDetails && jobsiteName && (
            <span className={classes.workerInfoItem}>
              <BriefcaseIcon
                className={classes.workerInfoItemIcon}
                aria-hidden="true"
              />
              <span className="odin-truncate">{jobsiteName}</span>
            </span>
          )}
        </div>
      </div>
    </>
  );

  return (
    <li className={classes.itemContainer}>
      {onClick ? (
        <button
          type="button"
          className={classes.workerInfo(true)}
          onClick={onClick}
        >
          {workerInfo}
        </button>
      ) : (
        <div className={classes.workerInfo(false)}>{workerInfo}</div>
      )}
      <div className={classes.removeButtonContainer}>
        <Button
          theme="naked"
          icon={TimesIcon}
          className={classes.removeButton}
          aria-label={`Remove ${name}`}
          onClick={onRemoveIconClick}
        />
      </div>
    </li>
  );
}
